/** PaceGroups — compact table of naturally-clustered pace groups (server computed) */
import { useCallback } from 'react';
import { formatLapTime } from './ResultsTable';
import type { PaceGroup } from '@/types/telemetry';
import { useI18n } from '@/context/I18nContext';

interface PaceGroupsProps {
  paceGroups?: PaceGroup[];
  fastestMs?: number;
  activeDriverIds: ReadonlySet<string>;
  onClick: (driverIds: Set<string>) => void;
}

export function PaceGroups({ paceGroups = [], fastestMs = 0, activeDriverIds, onClick }: PaceGroupsProps) {
  const { t } = useI18n();

  const handleClick = useCallback((group: PaceGroup) => {
    onClick(new Set(group.allDriverIds || []));
  }, [onClick]);

  if (!paceGroups || paceGroups.length === 0) {
    return (
      <div className="glass-card p-8 text-center">
        <p className="text-sm text-text-muted">{t('races.paceNoData')}</p>
      </div>
    );
  }

  return (
    <div className="glass-card p-0 overflow-hidden">
      {paceGroups.map((group, i) => {
        const groupDriverIds = group.allDriverIds || [];
        const isActive = groupDriverIds.some(id => activeDriverIds.has(id));

        // Build the range label
        let rangeLabel: string;
        if (group.minDeltaMs === 0 && group.maxDeltaMs === 0) {
          rangeLabel = formatLapTime(fastestMs);
        } else if (group.minDeltaMs === 0) {
          rangeLabel = `${formatLapTime(fastestMs)} — +${(group.maxDeltaMs / 1000).toFixed(2)}s`;
        } else if (group.minDeltaMs === group.maxDeltaMs) {
          rangeLabel = `+${(group.minDeltaMs / 1000).toFixed(2)}s`;
        } else {
          rangeLabel = `+${(group.minDeltaMs / 1000).toFixed(2)}s — +${(group.maxDeltaMs / 1000).toFixed(2)}s`;
        }

        return (
          <div
            key={i}
            className={`
              flex items-center gap-4 px-4 py-2.5 transition-colors cursor-pointer select-none
              ${i > 0 ? 'border-t border-border-subtle/50' : ''}
              ${isActive ? 'bg-accent/[0.06]' : ''}
            `}
            onClick={() => handleClick(group)}
          >
            {/* Range label */}
            <div className="w-36 shrink-0">
              <span className="text-[11px] mono text-text-muted">{rangeLabel}</span>
            </div>

            {/* Driver circles */}
            <div className="flex flex-wrap gap-1.5 flex-1 min-w-0">
              {(group.drivers || []).map(d => (
                <div
                  key={d.driverId}
                  className={`
                    w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold mono
                    transition-colors shrink-0
                    ${isActive
                      ? 'bg-accent/20 text-accent ring-1 ring-accent/40'
                      : 'bg-bg-elevated text-text-primary border border-border-subtle'
                    }
                  `}
                  title={`#${d.kartNumber} — ${formatLapTime(d.bestLapMs)}${d.deltaMs > 0 ? ` (+${(d.deltaMs / 1000).toFixed(3)})` : ''}`}
                >
                  {d.kartNumber}
                </div>
              ))}
            </div>

            {/* Count */}
            <span className="text-[10px] text-text-muted tabular-nums shrink-0 w-4 text-right">
              {group.drivers?.length || 0}
            </span>
          </div>
        );
      })}
    </div>
  );
}
